/**
 * Language Context
 *
 * React context for i18n language management.
 * Loads locale JSON dynamically, persists selection to localStorage.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  Language,
  initLanguage,
  setLanguage as setLangPersist,
  setTranslations,
  processLocaleData,
  t as translate,
} from '../lib/i18n';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Cache loaded locale data so we do not re-import on every switch
const localeCache: Partial<Record<Language, Record<string, string>>> = {};

async function loadLocale(lang: Language): Promise<Record<string, string>> {
  if (localeCache[lang]) {
    return localeCache[lang]!;
  }

  let raw: Record<string, unknown>;

  if (lang === 'sw') {
    raw = (await import('../locales/sw.json')).default;
  } else {
    raw = (await import('../locales/en.json')).default;
  }

  const flat = processLocaleData(raw);
  localeCache[lang] = flat;
  return flat;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => initLanguage());
  // Counter to trigger re-render when translations change
  const [, setLoadCounter] = useState(0);

  // Load translations when language changes
  useEffect(() => {
    let cancelled = false;

    loadLocale(language).then((flat) => {
      if (cancelled) return;
      setTranslations(flat);
      setLoadCounter((c) => c + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLangPersist(lang);
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => translate(key, params),
    // Re-bind when loadCounter bumps so components get fresh translations
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
